import { useEffect, useRef, useState } from 'react'

import { ChartFlipper } from '../chart-flipper'
import { Legend } from '../common/legend/legend'
import { SideBySideChartContainer } from '../side-by-side-chart-container'
import { SplitChartFilterBar } from '../split-chart-filter-bar'
import { LineChartWithTooltip } from './line-chart-with-tooltip'
import { lineDataService } from './line-data.service'
import { lineColorService } from './line-color.service'

import { useStyles } from './line-block-split.style'

export const LineBlockSplit = function ({
  dataKey,
  filterDefinition,
  strata,
  chartData,
  tableData,
  showTableToggle,
  showTable,
  legendToggles,
  onLegendToggle,
  onSplitFilterChange,
  tableProps,
  displayOnly,
  ...props
}) {
  const classes = useStyles()
  props.marginTop = 16
  const tableMarginTop = 12

  const [tooltipCoordinates, setTooltipCoordinates] = useState()
  const [selectedX, setSelectedX] = useState()
  const [mergedSeries, setMergedSeries] = useState([])
  const [colors, setColors] = useState([])
  const seriesColorCache = useRef({})
  const nextColorIndex = useRef(0)

  useEffect(() => {
    const merged = lineDataService.getMergedSeries(chartData, strata)
    // Prime the seriesColorCache based on the merged series
    const { nextIndex, colors } = lineColorService.setChartColors(merged, seriesColorCache.current, nextColorIndex.current)
    // Then apply the colors from the seriesColorCache to each side's chart data
    chartData?.forEach((s) => {
      lineColorService.setChartColors(s, seriesColorCache.current, nextColorIndex.current)
    })
    nextColorIndex.current = nextIndex
    setMergedSeries(merged)
    setColors(colors)
  }, [ chartData, strata ])

  function onTooltipChange (tooltipCoordinates, selectedX) {
    setTooltipCoordinates(tooltipCoordinates)
    setSelectedX(selectedX)
  }

  if (!chartData) {
    return null
  }

  const legendLabels = mergedSeries.map((s) => s.label || s.name)

  const filteredChartData = [ ...chartData ]
  filteredChartData[0] = lineDataService.filterToggledChartSeries(chartData[0], legendToggles)
  filteredChartData[1] = lineDataService.filterToggledChartSeries(chartData[1], legendToggles)

  const filteredTableData = [ ...tableData ]
  filteredTableData[0] = lineDataService.filterToggledTableColumns(tableData[0], legendToggles)
  filteredTableData[1] = lineDataService.filterToggledTableColumns(tableData[1], legendToggles)

  return (
    <>
      <Legend
        labels={legendLabels}
        colors={colors}
        toggleDefaults={legendToggles}
        onToggle={onLegendToggle}
      />
      <SplitChartFilterBar
        filterDefinition={filterDefinition}
        onChange={onSplitFilterChange}
        className={classes.sideBySideChartFilterBar}
        displayOnly={displayOnly}
      />
      {filteredChartData && (
        <SideBySideChartContainer>
          {filteredChartData[0].length > 0 ? (
            <div className={classes.chart}>
              <ChartFlipper
                isHalf
                dataKey={dataKey}
                enabled={showTableToggle}
                showTable={showTable}
                tableData={filteredTableData[0]}
                tableProps={tableProps}
                tableStyles={{ marginTop: tableMarginTop }}
                tableOffset={12}
              >
                <div data-qa="side-by-side-chart">
                  {filteredChartData && (
                    <LineChartWithTooltip
                      series={filteredChartData[0]}
                      colors={colors}
                      legendToggles={legendToggles}
                      tooltipCoordinates={tooltipCoordinates}
                      onTooltipChange={onTooltipChange}
                      selectedX={selectedX}
                      {...props}
                    />
                  )}
                </div>
              </ChartFlipper>
            </div>
          ) : (
            <span className={classes.noDataMessage}>
            No data found for this filter combination
            </span>
          )}
          {filteredChartData[1].length > 0 ? (
            <div className={classes.chart}>
              <ChartFlipper
                isHalf
                dataKey={dataKey}
                enabled={showTableToggle}
                showTable={showTable}
                tableData={filteredTableData[1]}
                tableProps={tableProps}
                tableStyles={{ marginTop: tableMarginTop }}
                tableOffset={12}
              >
                <div data-qa="side-by-side-chart">
                  {filteredChartData[1] && (
                    <LineChartWithTooltip
                      series={filteredChartData[1]}
                      colors={colors}
                      legendToggles={legendToggles}
                      tooltipCoordinates={tooltipCoordinates}
                      onTooltipChange={onTooltipChange}
                      selectedX={selectedX}
                      {...props}
                      labelY={null}
                    />
                  )}
                </div>
              </ChartFlipper>
            </div>
          ) : (
            <span className={classes.noDataMessage}>
            No data found for this filter combination
            </span>
          )}
        </SideBySideChartContainer>
      )}
    </>
  )
}
